import { useState } from "react";
import axios from "axios";
import { API_BASE } from "../../lib/api";
import { Loader2 } from "lucide-react";
import { Logo } from "../Logo";
import { FIELD } from "../../hooks/useSimpleForm";
import { formatApiErrorDetail, ALERT_BOX } from "./format";

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.81-.07-1.59-.21-2.34H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3.02h3.86c2.26-2.09 3.56-5.17 3.56-8.77z" />
    <path fill="#34A853" d="M12 23.4c3.03 0 5.58-1 7.44-2.71l-3.86-3.02c-1.05.72-2.37 1.16-3.58 1.16-2.94 0-5.4-1.86-6.3-4.46l-3.66 2.85C3.84 20.88 7.62 23.4 12 23.4z" />
    <path fill="#FBBC05" d="M5.7 14.37A7.16 7.16 0 0 1 5.28 12c0-.83.15-1.63.42-2.37L2.04 6.78A11.94 11.94 0 0 0 .6 12c0 1.94.46 3.77 1.44 5.22l3.66-2.85z" />
    <path fill="#EA4335" d="M12 4.74c2.14 0 3.6.92 4.43 1.7l3.24-3.16C17.66 1.4 15.03.6 12 .6 7.62.6 3.84 3.12 2.04 6.78l3.66 2.85C6.6 6.6 9.06 4.74 12 4.74z" />
  </svg>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");

  const submitLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setLoginError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onLogin(data.user);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-10" data-testid="admin-login-screen">
      <div className="w-full max-w-md rounded-3xl border border-navy/10 bg-white p-8 text-center shadow-xl shadow-navy/10 sm:p-10">
        <div className="flex justify-center">
          <Logo />
        </div>
        <h1 className="mt-8 font-display text-2xl font-bold text-navy">Espace propriétaire</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">Consultez les demandes reçues via le formulaire du site.</p>

        <form onSubmit={submitLogin} className="mt-8 space-y-4 text-left" data-testid="admin-login-form">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-navy" htmlFor="admin-email">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" className={FIELD} placeholder="site.com@outlook.fr" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-navy" htmlFor="admin-password">Mot de passe</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" className={FIELD} placeholder="••••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          {loginError && (
            <p className={`${ALERT_BOX} px-4 py-3`} data-testid="admin-login-error">{loginError}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-navy-light hover:shadow-lg hover:shadow-gold/25 disabled:opacity-60"
          >
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? "Connexion…" : "Se connecter"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-navy/10" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">ou</span>
          <span className="h-px flex-1 bg-navy/10" />
        </div>

        <button
          data-testid="admin-login-button"
          onClick={googleLogin}
          className="flex w-full items-center justify-center gap-3 rounded-full border border-navy/15 bg-white px-6 py-3.5 text-sm font-semibold text-navy transition-all hover:border-gold hover:shadow-lg hover:shadow-gold/10"
        >
          <GoogleIcon /> Continuer avec Google
        </button>
      </div>
    </div>
  );
};
